/**
 * A mobile scan's coverage, under a bar that picks one area: tabs for the map of the areas, an
 * overview (the survey's figures and the areas table) and the plots forun drew for the picked area.
 * An area is picked in the bar or on the map.
 */
import { useEffect, useMemo, useState } from 'react';
import { Anchor, Button, Center, Group, Image, Loader, ScrollArea, Select, SimpleGrid, Stack, Table, Tabs, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '@/components/EmptyState';
import { StatCard } from '@/components/StatCard';
import { useFormat } from '@/components/useFormat';
import { SCAN_AREA_ID, type LayerOptions, type LayerRef } from '@/map/layers';
import type { LinkSource } from '@/map/linkSource';
import { MapView } from '@/map/MapView';
import { brand, forest } from '@/theme/theme';
import { unitsBounds } from './growing/data';
import { MapFrame } from './MapFrame';
import type { KindReportProps } from './ReportPage';
import { scanCoverageConfig, SCAN_SECTIONS } from './scan/config';
import { AREA_PLOTS, COVERAGE_FILE, plotPath, scanAreas, useScanData, type AreaPlot, type ScanArea, type ScanSummary } from './scan/data';
import { Section } from './trees/Section';
import { usePrinting } from './trees/usePrinting';

type ScanTab = 'map' | 'overview' | 'plots';

export function MlsCoverageReport({ inputs = [], linkSource, options, onOptionsChange, tokenSource, config }: KindReportProps) {
  const { t } = useTranslation('reports');
  const cfg = useMemo(() => scanCoverageConfig(config), [config]);
  const scan = inputs.find((i) => i.kind === 'lidar_mls' && i.available)?.object;
  const data = useScanData(scan, linkSource);
  const areas = useMemo(() => (data.areas ? scanAreas(data.areas) : []), [data.areas]);
  const [selected, setSelected] = useState<number | null>(null);
  const [focus, setFocus] = useState<[number, number, number, number] | null>(null);
  const [active, setActive] = useState<ScanTab | null>(null);
  const printing = usePrinting();
  const refs = useMemo<LayerRef[]>(() => (scan ? [{ object: scan, object_kind: 'lidar_mls', via: 'link', path: COVERAGE_FILE, digest: '' }] : []), [scan]);
  const vectors = useMemo(() => (scan && data.areas ? { [scan]: data.areas } : undefined), [scan, data.areas]);

  const s = cfg.sections;
  if (!SCAN_SECTIONS.some((k) => s[k].enabled)) return <EmptyState title={t('scan.nothingEnabled')} />;
  if (data.loading) {
    return (
      <Center py="md">
        <Loader color="forest" />
      </Center>
    );
  }
  if (data.failed || !scan) return <EmptyState title={t('scan.unreadable')} />;

  // The selection is the page's, not part of the view a viewer's browser keeps.
  const change = (next: LayerOptions) => {
    const rest = { ...next };
    delete rest.selectedArea;
    onOptionsChange(rest);
  };

  const zoomTo = (id: number) => data.areas && setFocus(unitsBounds(data.areas, new Set([String(id)]), (p) => String(p[SCAN_AREA_ID] ?? '')));
  // A pick from the bar brings the area into view; one clicked on the map already is.
  const select = (id: number | null) => {
    setSelected(id);
    if (id === null) setFocus(null);
    else if (s.map.enabled) zoomTo(id);
  };

  const tabs: ScanTab[] = [];
  if (s.map.enabled) tabs.push('map');
  if (s.areas.enabled || (s.summary.enabled && data.summary)) tabs.push('overview');
  if (s.plots.enabled) tabs.push('plots');
  const current = active && tabs.includes(active) ? active : tabs[0];
  // The map is mounted only while its tab shows (a hidden map has no size to fit to), so it opens on the pick.
  const open = (tab: ScanTab) => {
    setActive(tab);
    if (tab === 'map' && tab !== current && selected !== null) zoomTo(selected);
  };

  const panel = (tab: ScanTab) => {
    if (tab === 'map') {
      return (
        <>
          <Text className="print-only" fw={700} mb="xs">
            {t('scan.sections.map')}
          </Text>
          <MapFrame testId="report-mls_coverage" wide={!!options.wide} onWideChange={(wide) => change({ ...options, wide })}>
            <MapView
              refs={refs}
              linkSource={linkSource}
              externalVectors
              vectors={vectors}
              featurePopup={false}
              focus={focus}
              onFeatureClick={(click) => {
                const id = Number(click.properties.area_id);
                if (click.objectId === scan && Number.isFinite(id)) setSelected(id);
              }}
              options={{ ...options, selectedArea: selected ?? undefined }}
              onOptionsChange={change}
              tokenSource={tokenSource}
            />
          </MapFrame>
        </>
      );
    }
    if (tab === 'overview') {
      return (
        <Stack gap="xl">
          {s.summary.enabled && data.summary && <SummarySection summary={data.summary} areas={areas} />}
          {s.areas.enabled && <AreasSection areas={areas} selected={selected} />}
        </Stack>
      );
    }
    // A hint to click is no use on paper.
    return printing && selected === null ? null : <PlotsSection object={scan} area={selected} linkSource={linkSource} />;
  };

  return (
    <Stack gap="xl">
      {areas.length > 0 && (s.map.enabled || s.areas.enabled || s.plots.enabled) && (
        <AreaPicker areas={areas} selected={selected} onChange={select} onZoom={s.map.enabled && selected !== null ? () => zoomTo(selected) : undefined} />
      )}
      {tabs.length === 1 ? (
        panel(tabs[0])
      ) : (
        // Every panel stays in the page for print CSS to unhide; only the active one has content on screen.
        <Tabs value={current} onChange={(v) => v && open(v as ScanTab)} keepMounted className="report-tabs">
          <Tabs.List mb="md">
            {tabs.map((tab) => (
              <Tabs.Tab key={tab} value={tab}>
                {t(`scan.tabs.${tab}`)}
              </Tabs.Tab>
            ))}
          </Tabs.List>
          {tabs.map((tab) => (
            <Tabs.Panel key={tab} value={tab}>
              {(tab === current || printing) && panel(tab)}
            </Tabs.Panel>
          ))}
        </Tabs>
      )}
    </Stack>
  );
}

/** The area whose plots the page shows: a searchable list, a zoom to it and a way to drop it. */
function AreaPicker({ areas, selected, onChange, onZoom }: { areas: ScanArea[]; selected: number | null; onChange: (id: number | null) => void; onZoom?: () => void }) {
  const { t } = useTranslation('reports');
  const { number } = useFormat();
  const data = useMemo(
    () =>
      areas.map((a) => ({
        value: String(a.id),
        label: a.areaHa === null ? t('scan.picker.option', { area: a.id }) : t('scan.picker.optionHa', { area: a.id, value: number(a.areaHa, 2) }),
      })),
    [areas, t, number],
  );
  return (
    <Group align="flex-end" gap="sm" wrap="wrap" className="report-actions">
      <Select
        label={t('scan.picker.label')}
        placeholder={t('scan.picker.search')}
        searchable
        data={data}
        value={selected === null ? null : String(selected)}
        onChange={(v) => onChange(v === null ? null : Number(v))}
        style={{ flex: 1, minWidth: 260, maxWidth: 640 }}
        data-testid="scan-picker"
      />
      {selected !== null && (
        <>
          {onZoom && (
            <Button variant="default" onClick={onZoom} data-testid="scan-zoom">
              {t('scan.picker.zoom')}
            </Button>
          )}
          <Button variant="subtle" onClick={() => onChange(null)} data-testid="scan-clear">
            {t('scan.picker.clear')}
          </Button>
        </>
      )}
    </Group>
  );
}

function SummarySection({ summary, areas }: { summary: ScanSummary; areas: ScanArea[] }) {
  const { t } = useTranslation('reports');
  const { number } = useFormat();
  const c = summary.coverage;
  const areaHa = areas.reduce((sum, a) => sum + (a.areaHa ?? 0), 0);
  const tierSub = (min: number | null | undefined) => (min == null ? undefined : t('scan.kpi.tierSub', { value: number(min) }));
  return (
    <Section title={t('scan.sections.summary')}>
      <SimpleGrid cols={{ base: 1, xs: 2, md: 4 }} spacing="md">
        {summary.points !== null && <StatCard label={t('scan.kpi.points')} value={number(summary.points / 1e6)} unit={t('scan.units.million')} accent testId="scan-stat-points" />}
        {summary.files !== null && <StatCard label={t('scan.kpi.files')} value={number(summary.files)} />}
        <StatCard label={t('scan.kpi.areas')} value={number(areas.length)} sub={t('scan.kpi.areasSub', { value: number(areaHa, 2) })} testId="scan-stat-areas" />
        {summary.dtm && (
          <StatCard
            label={t('scan.kpi.dtm')}
            value={number(summary.dtm.medianM, 2)}
            unit="m"
            sub={summary.dtm.madM === null ? undefined : t('scan.kpi.dtmSub', { value: number(summary.dtm.madM, 2) })}
          />
        )}
        {c.extent && <StatCard label={t('scan.kpi.extent')} value={number(c.extent.areaHa, 1)} unit="ha" sub={tierSub(c.extent.minPointsM2)} />}
        {c.edge && <StatCard label={t('scan.kpi.edge')} value={number(c.edge.areaHa, 1)} unit="ha" sub={tierSub(c.edge.minPointsM2)} />}
        {c.good && <StatCard label={t('scan.kpi.good')} value={number(c.good.areaHa, 1)} unit="ha" sub={tierSub(c.good.minPointsM2)} />}
        {c.core && <StatCard label={t('scan.kpi.core')} value={number(c.core.areaHa, 1)} unit="ha" sub={tierSub(c.core.minPointsM2)} />}
      </SimpleGrid>
    </Section>
  );
}

function AreasSection({ areas, selected }: { areas: ScanArea[]; selected: number | null }) {
  const { t } = useTranslation('reports');
  const { number } = useFormat();
  const f = (v: number | null, digits = 0, scale = 1) => (v === null ? '—' : number(v / scale, digits));
  return (
    <Section title={t('scan.sections.areas')}>
      <ScrollArea>
        <Table highlightOnHover verticalSpacing="xs" fz="sm">
          <Table.Thead>
            <Table.Tr>
              {(['area', 'areaHa', 'goodShare', 'pointsIn', 'pointsOut', 'density', 'dtmMedian', 'dtmMad', 'dtmTilt'] as const).map((k) => (
                <Table.Th key={k}>{t(`scan.table.${k}`)}</Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {areas.map((a) => (
              <Table.Tr
                key={a.id}
                data-testid={`scan-area-${a.id}`}
                aria-selected={a.id === selected}
                style={{ background: a.id === selected ? forest[0] : undefined }}
              >
                <Table.Td fw={600}>{a.id}</Table.Td>
                <Table.Td>{f(a.areaHa, 2)}</Table.Td>
                <Table.Td>{a.goodShare === null ? '—' : `${number(a.goodShare * 100)} %`}</Table.Td>
                <Table.Td>{f(a.pointsIn, 0, 1e6)}</Table.Td>
                <Table.Td>{f(a.pointsOut, 0, 1e6)}</Table.Td>
                <Table.Td>{f(a.densityPerHa, 0, 1e6)}</Table.Td>
                <Table.Td>{f(a.dtmMedianM, 2)}</Table.Td>
                <Table.Td>{f(a.dtmMadM, 2)}</Table.Td>
                <Table.Td>{f(a.dtmTiltPer100M, 2)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </Section>
  );
}

function PlotsSection({ object, area, linkSource }: { object: string; area: number | null; linkSource: LinkSource }) {
  const { t } = useTranslation('reports');
  return (
    <Section title={area === null ? t('scan.sections.plots') : t('scan.plots.title', { area })}>
      {area === null ? (
        <Text c={brand.textMuted} data-testid="scan-plots-hint">
          {t('scan.plots.hint')}
        </Text>
      ) : (
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
          {AREA_PLOTS.map((plot) => (
            <Plot key={`${area}-${plot}`} object={object} area={area} plot={plot} linkSource={linkSource} />
          ))}
        </SimpleGrid>
      )}
    </Section>
  );
}

function Plot({ object, area, plot, linkSource }: { object: string; area: number; plot: AreaPlot; linkSource: LinkSource }) {
  const { t } = useTranslation('reports');
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    linkSource.url(object, plotPath(area, plot)).then(
      (u) => alive && setUrl(u),
      () => alive && setFailed(true),
    );
    return () => {
      alive = false;
    };
  }, [object, area, plot, linkSource]);
  const caption = t(`scan.plots.${plot}`);
  return (
    <Stack gap={4} data-testid={`scan-plot-${plot}`}>
      <Text fz={13} c={brand.textMuted}>
        {caption}
      </Text>
      {failed ? (
        <Text fz={13} c={brand.textMuted}>
          {t('scan.plots.unavailable')}
        </Text>
      ) : url ? (
        <Anchor href={url} target="_blank" rel="noreferrer" aria-label={t('scan.plots.open', { name: caption })}>
          <Image src={url} alt={caption} fit="contain" radius="md" />
        </Anchor>
      ) : (
        <Loader color="forest" size="sm" />
      )}
    </Stack>
  );
}
